import React from 'react'

import GeneralMultiChatPopup from '@/components/chat/general-multi-chat-popup'

import { WEBSOCKET_EVENTS } from '@/constant/websocket-events'
import { useAuth } from '@/contexts/auth-context'
import { useSocket } from '@/contexts/socket-context'
import { generalChatService } from '@/services/general-chat.service'
import tabNotificationService from '@/services/tab-notification.service'
import { extractMentionedUserIdsFromContent } from '@/utils/chat-mentions'
import { encodeMessageKeysetCursor, resolveKeysetHasMore, resolveKeysetHasPrevious } from '@/utils/chat-message-keyset'

import {
  ChatMessageData,
  Conversation,
  ConversationType,
  CreateConversationDto,
  CreateMessageDto,
  CreateMessageReactionDto,
  CreateReplyMessageDto,
  DeleteMessageScope,
  Message,
  MessageEmojiData,
  MessageKeysetListMeta,
  MessageReaction,
  MessageReactionType,
  MessageStatus,
  MessageType,
  ParticipantRole,
  PinnedMessage,
} from '@/types/chat.type'

interface ChatContextType {
  // Chat popup control
  // Enhanced openChat function that supports scrolling to specific conversations
  // When conversationId is provided, it will:
  // 1. Open the chat popup
  // 2. Select the specified conversation
  // 3. Scroll to that conversation in the conversation list
  // 4. Switch to the appropriate tab (direct/group/community) based on conversation type
  openChat: (
    targetUserId?: number,
    targetUserName?: string,
    conversationId?: number,
    jobName?: string,
    targetUserAvatar?: string,
    isLoadSelectedMessage?: boolean,
    messageId?: number
  ) => void

  // Connection and unread count state
  totalUnreadCount: number
  isConnected: boolean

  // Chat state management
  conversations: Conversation[]
  conversation: Conversation | null
  messages: Message[]
  isTyping: boolean
  typingUserName: string | null
  unreadCounts: Record<number, number>
  hasPreviousMessages: boolean
  hasMoreMessages: boolean
  contextMessagesEndIndex: number | null
  isLoading: boolean
  isLoadingPrevious: boolean
  isLoadingMore: boolean
  isPreviousMessageLoad: boolean
  isCreatingConversation: boolean
  hasLoadedConversations: boolean
  highlightedMessageId: number | null
  isLoadingContextMessages: boolean
  pendingScrollToMessage: { messageId: number; targetConversationId?: number } | null

  // Unread count management
  increaseUnreadCount: (increaseBy?: number) => void
  decreaseUnreadCount: (decreaseBy?: number) => void
  decreaseUnreadCountByConversation: (conversationId: number, decreaseBy: number) => void

  // Message actions
  sendMessage: (content: string) => Promise<void>
  sendMessageWithFile: (content: string, file: File) => Promise<void>
  sendReplyMessage: (content: string, repliedMessageId: number) => Promise<void>
  sendTypingIndicator: (isTyping: boolean) => void
  markConversationAsRead: (conversationId: number) => Promise<void>
  deleteMessage: (messageId: number, scope: DeleteMessageScope) => Promise<void>
  editMessage: (messageId: number, content: string) => Promise<void>
  reactToMessage: (messageId: number, reactionType: MessageReactionType, emojiData?: MessageEmojiData) => Promise<void>
  pinMessage: (messageId: number, conversationId: number) => Promise<void>
  unpinMessage: (messageId: number, conversationId: number) => Promise<void>
  getPinnedMessages: (conversationId: number) => Promise<PinnedMessage[]>

  // Conversation actions
  loadConversations: () => Promise<void>
  debouncedLoadConversations: () => Promise<void>
  loadPreviousMessages: () => Promise<void>
  loadMoreMessages: () => Promise<void>
  createConversation: (participantIds: number[], name?: string) => Promise<Conversation | undefined>
  createGroupChat: (
    name: string,
    description: string,
    participantIds: number[],
    type?: ConversationType,
    addAllUsers?: boolean,
    courseIds?: number[]
  ) => Promise<Conversation | undefined>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateConversation: (conversationId: number, updates: any) => Promise<void>
  deleteConversation: (conversationId: number, permanent?: boolean) => Promise<void>
  pinConversation: (conversationId: number) => Promise<void>
  unpinConversation: (conversationId: number) => Promise<void>
  searchConversations: (query: string) => Promise<Conversation[]>
  searchMessages: (query: string) => Promise<Message[]>
  reloadConversations: () => Promise<void>

  // Participant actions
  addParticipants: (conversationId: number, participantIds: number[]) => Promise<void>
  removeParticipants: (conversationId: number, participantIds: number[]) => Promise<void>

  // Conversation settings management
  leaveConversation: (conversationId: number) => Promise<void>
  updateParticipantRole: (conversationId: number, participantId: number, newRole: ParticipantRole) => Promise<void>

  // Connection management
  connect: () => void
  disconnect: () => void
  reconnect: () => void

  // Utility functions
  resetPagination: () => void

  // State setters for external updates
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>
  setConversation: (conversation: Conversation | null) => void
  setConversationId: (conversationId: number | undefined) => void
  setIsChatOpen: (isOpen: boolean) => void

  // WebSocket event handlers
  handleNewMessage: (message: Message) => void
  handleTypingIndicator: (data: { conversationId: number; userId: number; isTyping: boolean; userName?: string }) => void
  handleMessageDelivered: (data: { messageId: number; conversationId: number; deliveredTo: number }) => void
  handleMessageReaction: (data: { messageId: number; conversationId: number; reaction: MessageReaction }) => void
  handleMessageReactionBroadcast: (data: {
    messageId: number
    conversationId: number
    reaction: {
      user: { id: number; firstName: string; lastName: string; email: string }
      reactionType: MessageReactionType
      emojiData?: MessageEmojiData
    }
    isDeletion: boolean
  }) => void

  // Message navigation
  scrollToMessage: (messageId: number, conversationId?: number) => void
  scrollToConversation: (conversationId: number) => void
}

const ChatContext = React.createContext<ChatContextType | undefined>(undefined)

interface ChatProviderProps {
  children: React.ReactNode
}

// Constants for better configuration
const CHAT_CONSTANTS = {
  MESSAGES_PER_PAGE: 10,
  DEFAULT_MESSAGE_LIMIT: 50,
  MAX_MESSAGE_LIMIT: 100,
  SCROLL_THRESHOLD: 100,
  TYPING_TIMEOUT: 30000,
  TYPING_REFRESH_INTERVAL: 60000,
  RECONNECT_ATTEMPTS: 3,
  HEARTBEAT_INTERVAL: 30000,
} as const

// OPTIMIZATION STRATEGY for loadConversations:
// 1. Use hasLoadedConversations flag to prevent unnecessary reloads
// 2. Debounce external calls to prevent rapid successive API calls
// 3. Only reload conversations when new conversations are actually created
// 4. Use forceReload flag for explicit reloads (e.g., after creating new conversation)
// 5. Prevent loading when already in progress

// Helper function to format lastMessageContent for file messages
export const formatLastMessageContent = (message: Message | undefined, currentUserId: number): string | undefined => {
  if (!message) return undefined

  // API create responses may omit sender; fall back to generic labels
  if (!message.sender) {
    switch (message.type) {
      case MessageType.FILE:
        return 'Sent a file'
      case MessageType.IMAGE:
        return 'Sent an image'
      case MessageType.AUDIO:
        return 'Sent an audio message'
      case MessageType.VIDEO:
        return 'Sent a video'
      default:
        return message.content
    }
  }

  const isOwnMessage = message.sender.id === currentUserId
  const senderName = isOwnMessage ? 'You' : message.sender.firstName

  switch (message.type) {
    case MessageType.FILE:
      return isOwnMessage ? 'You sent a file' : `${senderName} sent a file`
    case MessageType.IMAGE:
      return isOwnMessage ? 'You sent an image' : `${senderName} sent an image`
    case MessageType.AUDIO:
      return isOwnMessage ? 'You sent an audio message' : `${senderName} sent an audio message`
    case MessageType.VIDEO:
      return isOwnMessage ? 'You sent a video' : `${senderName} sent a video`
    default:
      return message.content
  }
}

/** Prefer API sender; keep optimistic sender when create response omits it. */
const mergeCreatedMessage = (apiMessage: Message, optimisticMessage: Message): Message => ({
  ...apiMessage,
  sender: apiMessage.sender ?? optimisticMessage.sender,
  isOptimistic: false,
})

// Helper function to move a conversation to the top of the list
const moveConversationToTop = (conversations: Conversation[], conversationId: number): Conversation[] => {
  const currentConversation = conversations.find(conv => conv.id === conversationId)
  if (currentConversation) {
    const otherConversations = conversations.filter(conv => conv.id !== conversationId)
    return [currentConversation, ...otherConversations]
  }
  return conversations
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const { subscribe, unsubscribe, isConnected: socketConnected } = useSocket()

  // Chat state management (merged from use-general-chat)
  const [conversations, setConversations] = React.useState<Conversation[]>([])
  const [conversation, setConversation] = React.useState<Conversation | null>(null)
  const [messages, setMessages] = React.useState<Message[]>([])
  const [isTyping, setIsTyping] = React.useState(false)
  const [typingUserName, setTypingUserName] = React.useState<string | null>(null)
  const [typingUsers, setTypingUsers] = React.useState<Record<number, string>>({})
  const typingUserTimeoutsRef = React.useRef<Record<number, NodeJS.Timeout>>({})
  const [unreadCounts, setUnreadCounts] = React.useState<Record<number, number>>({})
  const [hasPreviousMessages, setHasPreviousMessages] = React.useState(false)
  const [hasMoreMessages, setHasMoreMessages] = React.useState(false)
  const [contextMessagesEndIndex, setContextMessagesEndIndex] = React.useState<number | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isLoadingPrevious, setIsLoadingPrevious] = React.useState(false)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const [isPreviousMessageLoad, setIsPreviousMessageLoad] = React.useState(false)
  const [isCreatingConversation, setIsCreatingConversation] = React.useState(false)
  const [totalMessagesLoaded, setTotalMessagesLoaded] = React.useState(0)
  const previousCursorRef = React.useRef<string | null>(null)
  const [hasLoadedConversations, setHasLoadedConversations] = React.useState(false)
  const [isChatOpen, setIsChatOpen] = React.useState(false)
  const [highlightedMessageId, setHighlightedMessageId] = React.useState<number | null>(null)

  // State for handling reaction notification clicks
  const [isLoadingContextMessages, setIsLoadingContextMessages] = React.useState(false)
  const [pendingScrollToMessage, setPendingScrollToMessage] = React.useState<{
    messageId: number
    targetConversationId?: number
  } | null>(null)

  // Connection and unread count state
  const [totalUnreadCount, setTotalUnreadCount] = React.useState<number>(0)
  const [isConnected, setIsConnected] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (totalUnreadCount === 0) {
      tabNotificationService.clearChatUnreadBadge()
    } else {
      tabNotificationService.updateChatUnreadBadge(totalUnreadCount)
    }
  }, [totalUnreadCount])

  // Refs
  const typingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const isCurrentUserTypingRef = React.useRef(false)
  const lastTypingActivityAtRef = React.useRef<number>(0)
  const loadConversationsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const chatPopupRef = React.useRef<{
    openChat: (
      _targetUserId?: number,
      _targetUserName?: string,
      _conversationId?: number,
      _jobName?: string,
      _targetUserAvatar?: string
    ) => void
    scrollToMessage?: (_messageId: number) => void
    scrollToMessageWithRetries?: (_messageId: number) => void
    beginMessageNavigationScroll?: () => void
    totalUnreadCount: number
    isConnected: boolean
  }>({
    openChat: () => {
      // This will be overridden by the ref
    },
    totalUnreadCount: 0,
    isConnected: false,
  })

  const messageNavigationLoadStartedRef = React.useRef<number | null>(null)
  const pendingScrollClearTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const highlightClearTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPendingScrollTimeout = React.useCallback(() => {
    if (pendingScrollClearTimeoutRef.current) {
      clearTimeout(pendingScrollClearTimeoutRef.current)
      pendingScrollClearTimeoutRef.current = null
    }
  }, [])

  const clearHighlightTimer = React.useCallback(() => {
    if (highlightClearTimeoutRef.current) {
      clearTimeout(highlightClearTimeoutRef.current)
      highlightClearTimeoutRef.current = null
    }
  }, [])

  const scheduleMessageHighlight = React.useCallback(
    (messageId: number) => {
      clearHighlightTimer()
      setHighlightedMessageId(messageId)
      highlightClearTimeoutRef.current = setTimeout(() => {
        highlightClearTimeoutRef.current = null
        setHighlightedMessageId(null)
        chatPopupRef.current.beginMessageNavigationScroll?.()
      }, 2000)
    },
    [clearHighlightTimer]
  )

  const completePendingMessageNavigation = React.useCallback(
    (messageId: number) => {
      scheduleMessageHighlight(messageId)
      chatPopupRef.current.beginMessageNavigationScroll?.()
      chatPopupRef.current.scrollToMessageWithRetries?.(messageId)
    },
    [scheduleMessageHighlight]
  )

  const finishPendingMessageNavigation = React.useCallback(() => {
    clearPendingScrollTimeout()
    pendingScrollClearTimeoutRef.current = setTimeout(() => {
      setPendingScrollToMessage(null)
      messageNavigationLoadStartedRef.current = null
      pendingScrollClearTimeoutRef.current = null
    }, 2600)
  }, [clearPendingScrollTimeout])

  // Function to increase unread count when new messages arrive
  const increaseUnreadCount = React.useCallback((increaseBy = 1) => {
    setTotalUnreadCount(prev => prev + increaseBy)

    // Update the ref as well
    if (chatPopupRef.current) {
      chatPopupRef.current.totalUnreadCount = chatPopupRef.current.totalUnreadCount + increaseBy
    }
  }, [])

  // Load all conversations for the current user
  const loadConversations = React.useCallback(
    async (forceReload = false) => {
      if (!user?.id) return

      // Prevent reloading if conversations are already loaded and not forcing reload
      // Only skip if we have conversations AND we're not forcing a reload
      if (!forceReload && conversations.length > 0) {
        return
      }

      // Prevent loading if already in progress
      if (isLoading && !forceReload) {
        return
      }

      try {
        setIsLoading(true)
        const allConversations = await generalChatService.getConversations()

        const finalConversations = allConversations

        setConversations(finalConversations)
        setHasLoadedConversations(true)

        // OPTIMIZATION: Use batch API call instead of individual calls for each conversation
        if (finalConversations.length > 0) {
          try {
            const conversationIds = finalConversations.map(conv => conv.id)
            const unreadCountsResponse = await generalChatService.getUnreadCounts(conversationIds)

            // Transform the response to match our expected format
            const counts: Record<number, number> = {}
            let totalCount = 0

            Object.entries(unreadCountsResponse).forEach(([conversationId, unreadCount]) => {
              const convId = Number(conversationId)
              counts[convId] = unreadCount
              totalCount += unreadCount
            })

            setUnreadCounts(counts)
            setTotalUnreadCount(totalCount)

            // Update the ref so the popup can access the latest counts
            if (chatPopupRef.current) {
              chatPopupRef.current.totalUnreadCount = totalCount
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to get batch unread counts:', error)
          }
        } else {
          setUnreadCounts({})
          setTotalUnreadCount(0)
          if (chatPopupRef.current) {
            chatPopupRef.current.totalUnreadCount = 0
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load conversations:', error)
      } finally {
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, conversations.length]
  )

  // Force reload conversations (useful when new conversations are created)
  const reloadConversations = React.useCallback(async () => {
    // Clear any pending timeout
    if (loadConversationsTimeoutRef.current) {
      clearTimeout(loadConversationsTimeoutRef.current)
    }

    // Debounce the reload to prevent rapid successive calls
    loadConversationsTimeoutRef.current = setTimeout(async () => {
      setHasLoadedConversations(false)
      await loadConversations(true)
    }, 300) // 300ms debounce
  }, [loadConversations])

  // Debounced version of loadConversations for external calls
  const debouncedLoadConversations = React.useCallback(async () => {
    // Clear any pending timeout
    if (loadConversationsTimeoutRef.current) {
      clearTimeout(loadConversationsTimeoutRef.current)
    }

    // Debounce the load to prevent rapid successive calls
    loadConversationsTimeoutRef.current = setTimeout(async () => {
      await loadConversations()
    }, 200) // 200ms debounce
  }, [loadConversations])

  // Function to handle new messages from WebSocket
  const handleNewMessage = React.useCallback(
    (message: Message) => {
      try {
        // Only increase count for messages from other users
        if (message.sender.id !== user?.id) {
          increaseUnreadCount(1)

          // Update the individual conversation's unread count
          setUnreadCounts(prev => ({
            ...prev,
            [message.conversation.id]: (prev[message.conversation.id] || 0) + 1,
          }))

          // Reload conversations if this is a new conversation we don't have
          // OR if this is an existing conversation that might be filtered out due to being empty
          const existingConversation = conversations.find(conv => conv.id === message.conversation.id)
          if (!existingConversation) {
            reloadConversations()
          } else {
            // Check if the existing conversation is empty (no lastMessageContent or lastMessageAt)
            // If so, reload conversations to ensure it appears in the filtered list
            const isEmptyConversation = !existingConversation.lastMessageContent && !existingConversation.lastMessageAt
            if (isEmptyConversation) {
              reloadConversations()
            }
          }
        }
        // eslint-disable-next-line no-console
        console.log('New message received:', message)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling new message:', error)
      }
    },
    [user?.id, increaseUnreadCount, reloadConversations, conversations]
  )

  // Function to handle typing indicators
  const handleTypingIndicator = React.useCallback(
    (data: { conversationId: number; userId: number; isTyping: boolean; userName?: string }) => {
      try {
        // Only show typing indicator for the current conversation and from other users
        if (conversation?.id === data.conversationId && data.userId !== user?.id) {
          const clearTypingUserTimeout = (targetUserId: number) => {
            const existingTimeout = typingUserTimeoutsRef.current[targetUserId]
            if (existingTimeout) {
              clearTimeout(existingTimeout)
              delete typingUserTimeoutsRef.current[targetUserId]
            }
          }

          setTypingUsers(prev => {
            const next = { ...prev }
            if (data.isTyping) {
              clearTypingUserTimeout(data.userId)
              next[data.userId] = data.userName || 'Someone'
              typingUserTimeoutsRef.current[data.userId] = setTimeout(() => {
                setTypingUsers(currentTypingUsers => {
                  if (!(data.userId in currentTypingUsers)) {
                    return currentTypingUsers
                  }
                  const updatedTypingUsers = { ...currentTypingUsers }
                  delete updatedTypingUsers[data.userId]
                  return updatedTypingUsers
                })
                clearTypingUserTimeout(data.userId)
              }, CHAT_CONSTANTS.TYPING_TIMEOUT)
            } else {
              clearTypingUserTimeout(data.userId)
              delete next[data.userId]
            }
            return next
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling typing indicator:', error)
      }
    },
    [conversation?.id, user?.id]
  )

  React.useEffect(() => {
    setTypingUsers({})
    Object.values(typingUserTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId))
    typingUserTimeoutsRef.current = {}
  }, [conversation?.id])

  React.useEffect(() => {
    const names = Object.values(typingUsers)
    setIsTyping(names.length > 0)

    if (names.length === 0) {
      setTypingUserName(null)
      return
    }

    const displayedNames = names.slice(0, 2).join(', ')
    setTypingUserName(names.length > 2 ? `${displayedNames}...` : displayedNames)
  }, [typingUsers])

  // Function to handle new messages from WebSocket (message-sent event)
  const handleMessageSent = React.useCallback(
    async (messageData: ChatMessageData) => {
      try {
        // Check if conversation exists in current list first
        const existingConversation = conversations.find(conv => conv.id === messageData.conversation.id)

        // Handle messages from the current user - replace optimistic messages
        if (messageData.sender.id === user?.id) {
          // Check if this is an optimistic message that needs to be replaced
          const optimisticMessageIndex = messages.findIndex(
            msg =>
              msg.isOptimistic &&
              msg.tempId &&
              msg.sender.id === user.id &&
              // Match by content for text messages, or by type for file messages
              (msg.content === messageData.content || (msg.type === messageData.type && msg.type !== MessageType.TEXT))
          )

          if (optimisticMessageIndex !== -1) {
            // Replace the optimistic message with the real message
            const realMessage: Message = {
              id: messageData.id,
              content: messageData.content,
              type: messageData.type || MessageType.TEXT,
              status: messageData.status || MessageStatus.SENT,
              sender: messageData.sender,
              conversation: messageData.conversation,
              repliedMessage: messageData.repliedMessage,
              fileUrl: messageData.fileUrl,
              fileName: messageData.fileName,
              fileSize: messageData.fileSize,
              fileType: messageData.fileType,
              metadata: messageData.metadata,
              isEdited: messageData.isEdited || false,
              editedAt: messageData.editedAt,
              isDeleted: messageData.isDeleted || false,
              deletedAt: messageData.deletedAt,
              createdAt: messageData.createdAt || new Date().toISOString(),
              updatedAt: messageData.updatedAt || new Date().toISOString(),
              userName: messageData.userName,
              userAvatar: messageData.userAvatar,
              isSystem: messageData.isSystem || false,
              isOptimistic: false,
            }

            // Replace the optimistic message
            setMessages(prev => prev.map((msg, index) => (index === optimisticMessageIndex ? realMessage : msg)))

            // Update conversation's last message info with real message and move to top
            setConversations(prev => {
              const updatedConversations = prev.map(conv =>
                conv.id === messageData.conversation.id
                  ? {
                      ...conv,
                      lastMessageContent: formatLastMessageContent(realMessage, user?.id || 0),
                      lastMessageAt: realMessage.createdAt,
                    }
                  : conv
              )
              return moveConversationToTop(updatedConversations, messageData.conversation.id)
            })
          }

          // Still need to handle conversation reload for empty conversations even for current user messages
          if (!existingConversation?.id) {
            // If conversation doesn't exist in current list, reload conversations to fetch it
            await reloadConversations()
          } else {
            // Check if the existing conversation is empty and reload if needed
            if (messageData.isEmptyConversation) {
              await reloadConversations()
            }
          }
          return
        }

        // Check if message already exists (to avoid duplicates)
        const existingMessageIndex = messages.findIndex(msg => msg.id === messageData.id)

        if (existingMessageIndex === -1) {
          // eslint-disable-next-line no-console
          console.log('Adding new message to conversation:', messageData.content)

          // Validate required fields before conversion
          if (!messageData.id || !messageData.content || !messageData.sender) {
            // eslint-disable-next-line no-console
            console.error('Missing required fields in message data:', messageData)
            return
          }

          // Convert ChatMessageData to Message format
          const message: Message = {
            id: messageData.id,
            content: messageData.content,
            type: messageData.type || MessageType.TEXT,
            status: messageData.status || MessageStatus.SENT,
            sender: messageData.sender,
            conversation: messageData.conversation,
            repliedMessage: messageData.repliedMessage,
            fileUrl: messageData.fileUrl,
            fileName: messageData.fileName,
            fileSize: messageData.fileSize,
            fileType: messageData.fileType,
            metadata: messageData.metadata,
            isEdited: messageData.isEdited || false,
            editedAt: messageData.editedAt,
            isDeleted: messageData.isDeleted || false,
            deletedAt: messageData.deletedAt,
            createdAt: messageData.createdAt || new Date().toISOString(),
            updatedAt: messageData.updatedAt || new Date().toISOString(),
            userName: messageData.userName,
            userAvatar: messageData.userAvatar,
            isSystem: messageData.isSystem || false,
          }

          // Only add message to current messages if we have a conversation selected
          if (conversation?.id === messageData.conversation.id) {
            // Append new message to the end (for chronological order)
            setMessages(prev => [...prev, message])

            // Update total messages loaded when new message arrives
            setTotalMessagesLoaded(prev => prev + 1)

            // Only set hasPreviousMessages to true if we previously had no more messages
            if (hasPreviousMessages === false && totalMessagesLoaded % CHAT_CONSTANTS.MESSAGES_PER_PAGE === 0) {
              setHasPreviousMessages(true)
            }
          }

          if (existingConversation?.id) {
            // Update conversation's last message info in the conversations list and move to top
            setConversations(prev => {
              const updatedConversations = prev.map(conv =>
                conv.id === messageData.conversation.id
                  ? {
                      ...conv,
                      lastMessageContent: formatLastMessageContent(message, user?.id || 0),
                      lastMessageAt: message.createdAt,
                    }
                  : conv
              )
              return moveConversationToTop(updatedConversations, messageData.conversation.id)
            })
          }

          // Notify parent component about new message for real-time unread count updates
          handleNewMessage(message)
        } else {
          // eslint-disable-next-line no-console
          console.log('Updating existing message:', messageData.id)
          // Update existing message if it has changed
          setMessages(prev =>
            prev.map((msg, index) => (index === existingMessageIndex ? { ...msg, ...messageData } : msg))
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling message-sent event:', error)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversation?.id, user?.id, messages, hasPreviousMessages, totalMessagesLoaded, handleNewMessage]
  )

  // Function to handle message delivered confirmation
  const handleMessageDelivered = React.useCallback(
    (data: { messageId: number; conversationId: number; deliveredTo: number }) => {
      try {
        // Handle message delivered confirmation logic here if needed
        // eslint-disable-next-line no-console
        console.log('Message delivered confirmation:', data)

        // Update message delivery status in the current conversation
        if (conversation?.id === data.conversationId) {
          setMessages(prev =>
            prev.map(msg => (msg.id === data.messageId ? { ...msg, status: MessageStatus.DELIVERED } : msg))
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling message delivered confirmation:', error)
      }
    },
    [conversation?.id]
  )

  // Function to handle message reaction events
  const handleMessageReaction = React.useCallback(
    (data: { messageId: number; conversationId: number; reaction: MessageReaction }) => {
      try {
        // Handle message reaction logic here
        // eslint-disable-next-line no-console
        console.log('Message reaction received:', data)

        // Only update messages in the current conversation
        if (conversation?.id === data.conversationId) {
          setMessages(prev =>
            prev.map(msg => {
              if (msg.id === data.messageId) {
                // Check if this reaction already exists from the same user
                const existingReactionIndex = msg.reactions?.findIndex(
                  r => r.user.id === data.reaction.user.id && r.reactionType === data.reaction.reactionType
                )

                if (existingReactionIndex !== undefined && existingReactionIndex >= 0) {
                  // Replace existing reaction
                  const updatedReactions = [...(msg.reactions || [])]
                  updatedReactions[existingReactionIndex] = data.reaction
                  return { ...msg, reactions: updatedReactions }
                } else {
                  // Add new reaction
                  return {
                    ...msg,
                    reactions: [...(msg.reactions || []), data.reaction],
                  }
                }
              }
              return msg
            })
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling message reaction:', error)
      }
    },
    [conversation?.id]
  )

  // Function to handle message reaction broadcast events
  const handleMessageReactionBroadcast = React.useCallback(
    (data: {
      messageId: number
      conversationId: number
      reaction: {
        user: { id: number; firstName: string; lastName: string; email: string }
        reactionType: MessageReactionType
        emojiData?: MessageEmojiData
      }
      isDeletion: boolean
    }) => {
      try {
        // Only update messages in the current conversation
        if (conversation?.id === data.conversationId) {
          setMessages(prev =>
            prev.map(msg => {
              if (msg.id === data.messageId) {
                if (data.isDeletion) {
                  // Remove the reaction
                  return {
                    ...msg,
                    reactions:
                      msg.reactions?.filter(r => {
                        // For default reactions, only check reactionType and user
                        if (data.reaction.reactionType !== 'custom') {
                          return !(r.user.id === data.reaction.user.id && r.reactionType === data.reaction.reactionType)
                        }

                        // For custom reactions, check both reactionType and emoji identifier
                        if (r.user.id !== data.reaction.user.id || r.reactionType !== 'custom') {
                          return true
                        }

                        // Check if it's the same custom emoji by comparing identifiers
                        if (data.reaction.emojiData?.unicode && r.emojiData?.unicode) {
                          return r.emojiData.unicode !== data.reaction.emojiData.unicode
                        }

                        if (data.reaction.emojiData?.imageUrl && r.emojiData?.imageUrl) {
                          return r.emojiData.imageUrl !== data.reaction.emojiData.imageUrl
                        }

                        // Fallback to name comparison if available
                        if (data.reaction.emojiData?.name && r.emojiData?.name) {
                          return r.emojiData.name !== data.reaction.emojiData.name
                        }

                        return true
                      }) || [],
                  }
                } else {
                  // Add or update the reaction
                  const newReaction: MessageReaction = {
                    id: -1, // Temporary ID for broadcast reactions
                    user: {
                      id: data.reaction.user.id,
                      firstName: data.reaction.user.firstName,
                      lastName: data.reaction.user.lastName,
                      avatar: undefined, // Avatar not provided in broadcast
                    },
                    reactionType: data.reaction.reactionType,
                    emojiData: data.reaction.emojiData,
                    createdAt: new Date().toISOString(),
                  }

                  // Check if this reaction already exists from the same user
                  const existingReactionIndex = msg.reactions?.findIndex(r => {
                    // For default reactions, only check reactionType and user
                    if (data.reaction.reactionType !== 'custom') {
                      return r.user.id === data.reaction.user.id && r.reactionType === data.reaction.reactionType
                    }

                    // For custom reactions, check both reactionType and emoji identifier
                    if (r.user.id !== data.reaction.user.id || r.reactionType !== 'custom') {
                      return false
                    }

                    // Check if it's the same custom emoji by comparing identifiers
                    if (data.reaction.emojiData?.unicode && r.emojiData?.unicode) {
                      return r.emojiData.unicode === data.reaction.emojiData.unicode
                    }

                    if (data.reaction.emojiData?.imageUrl && r.emojiData?.imageUrl) {
                      return r.emojiData.imageUrl === data.reaction.emojiData.imageUrl
                    }

                    // Fallback to name comparison if available
                    if (data.reaction.emojiData?.name && r.emojiData?.name) {
                      return r.emojiData.name === data.reaction.emojiData.name
                    }

                    return false
                  })

                  if (existingReactionIndex !== undefined && existingReactionIndex >= 0) {
                    // Replace existing reaction
                    const updatedReactions = [...(msg.reactions || [])]
                    updatedReactions[existingReactionIndex] = newReaction
                    return { ...msg, reactions: updatedReactions }
                  } else {
                    // Add new reaction
                    return {
                      ...msg,
                      reactions: [...(msg.reactions || []), newReaction],
                    }
                  }
                }
              }
              return msg
            })
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling message reaction broadcast:', error)
      }
    },
    [conversation?.id]
  )

  // Function to scroll to a specific message (simplified version)
  const scrollToMessage = React.useCallback(
    async (messageId: number, targetConversationId?: number) => {
      try {
        // If a specific conversation is provided and it's different from current, switch to it first
        if (targetConversationId && targetConversationId !== conversation?.id) {
          // Find the conversation and switch to it
          const targetConversation = conversations.find(conv => conv.id === targetConversationId)
          if (targetConversation) {
            setConversation(targetConversation)
            // Open chat popup if not already open
            if (chatPopupRef.current) {
              chatPopupRef.current.openChat(undefined, undefined, targetConversationId)
            }
          }
        }

        // Check if the message is already in the current messages
        const existingMessage = messages.find(msg => msg.id === messageId)

        if (existingMessage) {
          completePendingMessageNavigation(messageId)
        } else {
          clearPendingScrollTimeout()
          messageNavigationLoadStartedRef.current = null
          chatPopupRef.current.beginMessageNavigationScroll?.()
          setPendingScrollToMessage({
            messageId,
            targetConversationId,
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error scrolling to message:', error)
      }
    },
    [clearPendingScrollTimeout, conversation?.id, conversations, messages, completePendingMessageNavigation]
  )

  // Function to scroll to a specific conversation in the list
  const scrollToConversation = React.useCallback(
    (conversationId: number) => {
      try {
        // Find the conversation and switch to it
        const targetConversation = conversations.find(conv => conv.id === conversationId)
        if (targetConversation) {
          setConversation(targetConversation)
          // Open chat popup if not already open
          if (chatPopupRef.current) {
            chatPopupRef.current.openChat(undefined, undefined, conversationId)
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error scrolling to conversation:', error)
      }
    },
    [conversations]
  )

  // Function to decrease unread count when a message is read
  const decreaseUnreadCount = React.useCallback((decreaseBy = 1) => {
    setTotalUnreadCount(prev => Math.max(0, prev - decreaseBy))

    // Update the ref as well
    if (chatPopupRef.current) {
      chatPopupRef.current.totalUnreadCount = Math.max(0, chatPopupRef.current.totalUnreadCount - decreaseBy)
    }
  }, [])

  // Function to decrease unread count by a specific amount for a conversation
  const decreaseUnreadCountByConversation = React.useCallback((conversationId: number, decreaseBy: number) => {
    // Update the total unread count
    setTotalUnreadCount(prev => Math.max(0, prev - decreaseBy))

    // Update the individual conversation unread count
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: Math.max(0, (prev[conversationId] || 0) - decreaseBy),
    }))

    // Update the ref as well
    if (chatPopupRef.current) {
      chatPopupRef.current.totalUnreadCount = Math.max(0, chatPopupRef.current.totalUnreadCount - decreaseBy)
    }
  }, [])

  // ===== Chat functionality merged from use-general-chat =====

  // Load initial messages for selected conversation
  const loadMessages = React.useCallback(
    async (targetMessageId?: number) => {
      if (!conversation?.id || !user?.id) return

      setIsLoading(true)
      previousCursorRef.current = null
      try {
        let allMessages: Message[] = []
        let initialMeta: MessageKeysetListMeta | undefined
        if (targetMessageId) {
          // If we need to load a specific message, get context messages first
          setIsLoadingContextMessages(true)
          try {
            const contextResponse = await generalChatService.getMessageWithContext(targetMessageId, {
              limit: 10,
            })
            const contextMessages = contextResponse.data
            initialMeta = contextResponse.meta

            if (contextMessages && contextMessages.length > 0) {
              previousCursorRef.current = contextResponse.meta?.previousCursor ?? null

              // Merge context messages with existing messages state, avoiding duplicates
              const existingIds = new Set(messages.map(msg => msg.id))
              const newContextMessages = contextMessages.filter(msg => !existingIds.has(msg.id))

              // Combine and sort all messages chronologically
              allMessages = [...newContextMessages, ...messages]

              // Find the index where context messages end
              // Context messages are the newly added ones that come before existing messages
              const contextEndIndex = allMessages.findIndex(msg => messages.some(m => m.id === msg.id)) - 1

              // Track where context messages end (before the existing messages)
              setContextMessagesEndIndex(contextEndIndex >= 0 ? contextEndIndex : null)
            } else {
              // Fallback to regular message loading if context fails
              const messagesResponse = await generalChatService.getMessages(conversation.id, {
                limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
              })
              allMessages = [...messagesResponse.data].reverse()
              initialMeta = messagesResponse.meta
              previousCursorRef.current = messagesResponse.meta?.previousCursor ?? null
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load message context:', error)
            // Fallback to regular message loading
            const messagesResponse = await generalChatService.getMessages(conversation.id, {
              limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
            })
            allMessages = [...messagesResponse.data].reverse()
            initialMeta = messagesResponse.meta
            previousCursorRef.current = messagesResponse.meta?.previousCursor ?? null
          } finally {
            setIsLoadingContextMessages(false)
          }
        } else {
          // Regular message loading
          const messagesResponse = await generalChatService.getMessages(conversation.id, {
            limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
          })
          allMessages = [...messagesResponse.data].reverse()
          setContextMessagesEndIndex(null)
          initialMeta = messagesResponse.meta
          previousCursorRef.current = messagesResponse.meta?.previousCursor ?? null
        }

        setMessages(allMessages)
        setTotalMessagesLoaded(allMessages.length)

        // When loading messages with context (like from getMessagesWithContext),
        // always show both load buttons to allow users to load older and newer messages
        if (targetMessageId) {
          setHasMoreMessages(true) // Show "Load More Messages" button for newer messages
          setHasPreviousMessages(
            resolveKeysetHasPrevious(initialMeta, allMessages.length, CHAT_CONSTANTS.MESSAGES_PER_PAGE)
          )
        } else {
          setHasMoreMessages(false)
          setHasPreviousMessages(
            resolveKeysetHasPrevious(initialMeta, allMessages.length, CHAT_CONSTANTS.MESSAGES_PER_PAGE)
          )
        }

        // Update conversation's last message info if there are messages
        if (allMessages.length > 0) {
          const lastMessage = allMessages[allMessages.length - 1] // Last message in chronological order
          setConversations(prev =>
            prev.map(conv =>
              conv.id === conversation.id
                ? {
                    ...conv,
                    lastMessageContent: formatLastMessageContent(lastMessage, user?.id || 0),
                    lastMessageAt: lastMessage.createdAt,
                  }
                : conv
            )
          )
        }

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load messages:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [conversation?.id, user?.id, messages]
  )

  // Load previous messages (pagination for older messages)
  const loadPreviousMessages = React.useCallback(async () => {
    if (!conversation?.id || !user?.id || isLoadingPrevious || !hasPreviousMessages) return

    setIsLoadingPrevious(true)
    setIsPreviousMessageLoad(true)
    try {
      const oldestMessage = messages[0]
      if (!oldestMessage) {
        setHasPreviousMessages(false)
        return
      }

      const cursor = previousCursorRef.current ?? encodeMessageKeysetCursor(oldestMessage)
      const messagesResponse = await generalChatService.getMessages(conversation.id, {
        limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
        cursor,
        direction: 'before',
      })
      const messagesData = messagesResponse.data
      previousCursorRef.current = messagesResponse.meta?.previousCursor ?? null

      if (messagesData.length > 0) {
        const newMessages = [...messagesData].reverse()
        setMessages(prev => [...newMessages, ...prev])

        if (contextMessagesEndIndex !== null) {
          setContextMessagesEndIndex(prevIndex => {
            if (prevIndex === null) return null
            return prevIndex + messagesData.length
          })
        }

        setTotalMessagesLoaded(prev => prev + messagesData.length)
        setHasPreviousMessages(
          resolveKeysetHasPrevious(
            messagesResponse.meta,
            messagesData.length,
            CHAT_CONSTANTS.MESSAGES_PER_PAGE
          )
        )
      } else {
        setHasPreviousMessages(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load more messages:', error)
      // Don't update hasPreviousMessages on error to allow retry
    } finally {
      setIsLoadingPrevious(false)
      setIsPreviousMessageLoad(false)
    }
  }, [
    conversation?.id,
    user?.id,
    isLoadingPrevious,
    hasPreviousMessages,
    contextMessagesEndIndex,
    messages,
  ])

  // Load more messages (subsequent messages after the latest loaded message)
  const loadMoreMessages = React.useCallback(async () => {
    if (!conversation?.id || !user?.id || isLoadingMore || !hasMoreMessages) return

    // Ensure previous-message load mode is disabled when loading newer messages
    setIsPreviousMessageLoad(false)

    setIsLoadingMore(true)
    try {
      let messagesResponse

      if (contextMessagesEndIndex !== null && messages.length > contextMessagesEndIndex + 1) {
        const lastContextMessage = messages[contextMessagesEndIndex]
        const firstInitialMessage = messages[contextMessagesEndIndex + 1]

        if (!lastContextMessage || !firstInitialMessage) {
          setHasMoreMessages(false)
          return
        }

        messagesResponse = await generalChatService.getMessages(conversation.id, {
          limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
          afterCursor: encodeMessageKeysetCursor(lastContextMessage),
          beforeCursor: encodeMessageKeysetCursor(firstInitialMessage),
        })
      } else {
        const newestMessage = messages[messages.length - 1]
        if (!newestMessage) {
          setHasMoreMessages(false)
          return
        }

        messagesResponse = await generalChatService.getMessages(conversation.id, {
          limit: CHAT_CONSTANTS.MESSAGES_PER_PAGE,
          cursor: encodeMessageKeysetCursor(newestMessage),
          direction: 'after',
        })
      }

      const messagesData = messagesResponse.data

      if (messagesData.length > 0) {
        const newMessages = [...messagesData]

        setMessages(prev => {
          if (contextMessagesEndIndex !== null && contextMessagesEndIndex < prev.length) {
            const beforeContext = prev.slice(0, contextMessagesEndIndex)
            const afterContext = prev.slice(contextMessagesEndIndex + 1)
            return [...beforeContext, ...newMessages, ...afterContext]
          }

          return [...prev, ...newMessages]
        })

        if (contextMessagesEndIndex !== null) {
          setContextMessagesEndIndex(prevIndex => {
            if (prevIndex === null) return null
            return prevIndex - 1 + newMessages.length
          })
        }

        setTotalMessagesLoaded(prev => {
          if (contextMessagesEndIndex !== null) {
            return prev - 1 + messagesData.length
          }
          return prev + messagesData.length
        })

        setHasMoreMessages(
          resolveKeysetHasMore(messagesResponse.meta, messagesData.length, CHAT_CONSTANTS.MESSAGES_PER_PAGE)
        )
      } else {
        setHasMoreMessages(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load newer messages:', error)
      // Don't update hasMoreMessages on error to allow retry
    } finally {
      setIsLoadingMore(false)
    }
  }, [conversation?.id, user?.id, messages, isLoadingMore, hasMoreMessages, contextMessagesEndIndex])

  const extractMentionedUserIds = React.useCallback(
    (content: string): number[] => {
      return extractMentionedUserIdsFromContent(content, conversation?.participants || [], user?.id)
    },
    [conversation?.participants, user?.id]
  )

  const getChatErrorMessage = React.useCallback((error: unknown, fallbackMessage: string): string => {
    type ChatErrorResponseData = {
      message?: string | string[]
      error?: string | { message?: string | string[]; details?: string }
      errors?: Array<string | { message?: string; constraints?: Record<string, string> }>
      details?: string
    }

    const maybeError = error as {
      response?: {
        data?: string | ChatErrorResponseData
      }
      message?: string
    }

    const responseData = maybeError?.response?.data

    if (typeof responseData === 'string' && responseData.trim().length > 0) {
      return responseData
    }

    const responseDataObject: ChatErrorResponseData | undefined =
      responseData && typeof responseData === 'object' && !Array.isArray(responseData)
        ? (responseData as ChatErrorResponseData)
        : undefined

    const apiMessage = responseDataObject?.message
    if (Array.isArray(apiMessage) && apiMessage.length > 0) {
      return apiMessage.filter(Boolean).join(', ')
    }
    if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
      return apiMessage
    }

    const apiError = responseDataObject?.error
    if (typeof apiError === 'string' && apiError.trim().length > 0) {
      return apiError
    }
    if (apiError && typeof apiError === 'object') {
      const nestedErrorMessage = apiError.message
      if (Array.isArray(nestedErrorMessage) && nestedErrorMessage.length > 0) {
        return nestedErrorMessage.filter(Boolean).join(', ')
      }
      if (typeof nestedErrorMessage === 'string' && nestedErrorMessage.trim().length > 0) {
        return nestedErrorMessage
      }
      if (typeof apiError.details === 'string' && apiError.details.trim().length > 0) {
        return apiError.details
      }
    }

    const responseErrors = responseDataObject?.errors
    if (Array.isArray(responseErrors) && responseErrors.length > 0) {
      const flattenedErrors = responseErrors
        .map(item => {
          if (typeof item === 'string') return item
          if (item?.message) return item.message
          if (item?.constraints) return Object.values(item.constraints).join(', ')
          return ''
        })
        .filter(Boolean)

      if (flattenedErrors.length > 0) {
        return flattenedErrors.join(', ')
      }
    }

    if (
      typeof responseDataObject?.details === 'string' &&
      responseDataObject.details.trim().length > 0
    ) {
      return responseDataObject.details
    }

    if (typeof maybeError?.message === 'string' && maybeError.message.trim().length > 0) {
      return maybeError.message
    }

    return fallbackMessage
  }, [])

  // Send message
  const sendMessage = React.useCallback(
    async (content: string) => {
      if (!conversation?.id || !user?.id || !content.trim()) {
        return
      }

      // Generate temporary ID for optimistic update
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Create optimistic message
      const optimisticMessage: Message = {
        id: -1, // Temporary ID
        content: content.trim(),
        type: MessageType.TEXT,
        status: MessageStatus.PENDING,
        sender: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar,
        },
        conversation: {
          id: conversation.id,
        },
        isEdited: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true,
        tempId: tempId,
      }

      // Add optimistic message immediately
      setMessages(prev => [...prev, optimisticMessage])
      setTotalMessagesLoaded(prev => prev + 1)

      // Update conversation's last message info optimistically and move to top
      setConversations(prev => {
        const updatedConversations = prev.map(conv =>
          conv.id === conversation.id
            ? {
                ...conv,
                lastMessageContent: formatLastMessageContent(optimisticMessage, user?.id || 0),
                lastMessageAt: optimisticMessage.createdAt,
              }
            : conv
        )
        return moveConversationToTop(updatedConversations, conversation.id)
      })

      try {
        const taggedUserIds = extractMentionedUserIds(content.trim())
        const messageData: CreateMessageDto = {
          conversationId: conversation.id,
          content: content.trim(),
          taggedUserIds,
          type: MessageType.TEXT,
        }

        const newMessage = await generalChatService.createMessage(messageData)
        const resolvedMessage = mergeCreatedMessage(newMessage, optimisticMessage)

        // Replace optimistic message with real message
        setMessages(prev => prev.map(msg => (msg.tempId === tempId ? resolvedMessage : msg)))

        // Update conversation's last message info with real message and move to top
        setConversations(prev => {
          const updatedConversations = prev.map(conv =>
            conv.id === conversation.id
              ? {
                  ...conv,
                  lastMessageContent: formatLastMessageContent(resolvedMessage, user?.id || 0),
                  lastMessageAt: resolvedMessage.createdAt,
                }
              : conv
          )
          return moveConversationToTop(updatedConversations, conversation.id)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', error)
        const errorMessage = getChatErrorMessage(error, 'Cannot send message')
        // eslint-disable-next-line no-console
        console.log('sendMessage parsed errorMessage:', errorMessage, (error as { response?: { data?: unknown } })?.response?.data)

        // Update optimistic message to show error
        setMessages(prev =>
          prev.map(msg =>
            msg.tempId === tempId
              ? {
                  ...msg,
                  status: MessageStatus.ERROR,
                  errorMessage,
                }
              : msg
          )
        )
      }
    },
    [conversation?.id, user?.id, user?.firstName, user?.lastName, user?.avatar, extractMentionedUserIds, getChatErrorMessage]
  )

  // Send reply message
  const sendReplyMessage = React.useCallback(
    async (content: string, repliedMessageId: number) => {
      if (!conversation?.id || !user?.id || !content.trim()) {
        return
      }

      // Generate temporary ID for optimistic update
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Find the replied message to include in the optimistic message
      const repliedMessage = messages.find(msg => msg.id === repliedMessageId)

      // Create optimistic reply message
      const optimisticMessage: Message = {
        id: -1, // Temporary ID
        content: content.trim(),
        type: MessageType.TEXT,
        status: MessageStatus.PENDING,
        sender: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar,
        },
        conversation: {
          id: conversation.id,
        },
        repliedMessage: repliedMessage
          ? {
              id: repliedMessage.id,
              content: repliedMessage.content,
              type: repliedMessage.type,
              sender: {
                id: repliedMessage.sender.id,
                firstName: repliedMessage.sender.firstName,
                lastName: repliedMessage.sender.lastName,
                avatar: repliedMessage.sender.avatar,
              },
              fileUrl: repliedMessage.fileUrl,
              fileName: repliedMessage.fileName,
            }
          : undefined,
        isEdited: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true,
        tempId: tempId,
      }

      // Add optimistic message immediately
      setMessages(prev => [...prev, optimisticMessage])
      setTotalMessagesLoaded(prev => prev + 1)

      // Update conversation's last message info optimistically and move to top
      setConversations(prev => {
        const updatedConversations = prev.map(conv =>
          conv.id === conversation.id
            ? {
                ...conv,
                lastMessageContent: formatLastMessageContent(optimisticMessage, user?.id || 0),
                lastMessageAt: optimisticMessage.createdAt,
              }
            : conv
        )
        return moveConversationToTop(updatedConversations, conversation.id)
      })

      try {
        const taggedUserIds = extractMentionedUserIds(content.trim())
        const replyData: CreateReplyMessageDto = {
          conversationId: conversation.id,
          repliedMessageId: repliedMessageId,
          content: content.trim(),
          taggedUserIds,
          type: MessageType.TEXT,
        }

        const newMessage = await generalChatService.createReplyMessage(replyData)
        const resolvedMessage = mergeCreatedMessage(newMessage, optimisticMessage)

        // Replace optimistic message with real message
        setMessages(prev => prev.map(msg => (msg.tempId === tempId ? resolvedMessage : msg)))

        // Update conversation's last message info with real message and move to top
        setConversations(prev => {
          const updatedConversations = prev.map(conv =>
            conv.id === conversation.id
              ? {
                  ...conv,
                  lastMessageContent: formatLastMessageContent(resolvedMessage, user?.id || 0),
                  lastMessageAt: resolvedMessage.createdAt,
                }
              : conv
          )
          return moveConversationToTop(updatedConversations, conversation.id)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send reply message:', error)
        const errorMessage = getChatErrorMessage(error, 'Cannot send reply message')
        // eslint-disable-next-line no-console
        console.log('sendReplyMessage parsed errorMessage:', errorMessage, (error as { response?: { data?: unknown } })?.response?.data)

        // Update optimistic message to show error
        setMessages(prev =>
          prev.map(msg =>
            msg.tempId === tempId
              ? {
                  ...msg,
                  status: MessageStatus.ERROR,
                  errorMessage,
                }
              : msg
          )
        )
      }
    },
    [conversation?.id, user?.id, user?.firstName, user?.lastName, user?.avatar, messages, extractMentionedUserIds, getChatErrorMessage]
  )

  // Send message with file
  const sendMessageWithFile = React.useCallback(
    async (content: string, file: File) => {
      if (!conversation?.id || !user?.id) {
        return
      }

      // Generate temporary ID for optimistic update
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Create optimistic message for file
      const optimisticMessage: Message = {
        id: -1, // Temporary ID
        content: content.trim() || `Sent a file`,
        type: MessageType.FILE,
        status: MessageStatus.PENDING,
        sender: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar,
        },
        conversation: {
          id: conversation.id,
        },
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        isEdited: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true,
        tempId: tempId,
      }

      // Add optimistic message immediately
      setMessages(prev => [...prev, optimisticMessage])
      setTotalMessagesLoaded(prev => prev + 1)

      // Update conversation's last message info optimistically and move to top
      setConversations(prev => {
        const updatedConversations = prev.map(conv =>
          conv.id === conversation.id
            ? {
                ...conv,
                lastMessageContent: formatLastMessageContent(optimisticMessage, user?.id || 0),
                lastMessageAt: optimisticMessage.createdAt,
              }
            : conv
        )
        return moveConversationToTop(updatedConversations, conversation.id)
      })

      try {
        const taggedUserIds = extractMentionedUserIds(content.trim())
        const messageData = {
          conversationId: conversation.id,
          content: content.trim() || `Sent a file`,
          taggedUserIds,
          file,
          metadata: {
            taggedUserIds,
            originalFileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          },
        }

        const newMessage = await generalChatService.createMessageWithFile(messageData)
        const resolvedMessage = mergeCreatedMessage(newMessage, optimisticMessage)

        // Replace optimistic message with real message
        setMessages(prev => prev.map(msg => (msg.tempId === tempId ? resolvedMessage : msg)))

        // Update conversation's last message info with real message and move to top
        setConversations(prev => {
          const updatedConversations = prev.map(conv =>
            conv.id === conversation.id
              ? {
                  ...conv,
                  lastMessageContent: formatLastMessageContent(resolvedMessage, user?.id || 0),
                  lastMessageAt: resolvedMessage.createdAt,
                }
              : conv
          )
          return moveConversationToTop(updatedConversations, conversation.id)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send message with file:', error)
        const errorMessage = getChatErrorMessage(error, 'Cannot send message')
        // eslint-disable-next-line no-console
        console.log(
          'sendMessageWithFile parsed errorMessage:',
          errorMessage,
          (error as { response?: { data?: unknown } })?.response?.data
        )

        // Update optimistic message to show error
        setMessages(prev =>
          prev.map(msg =>
            msg.tempId === tempId
              ? {
                  ...msg,
                  status: MessageStatus.ERROR,
                  errorMessage,
                }
              : msg
          )
        )

        throw error
      }
    },
    [conversation?.id, user?.id, user?.firstName, user?.lastName, user?.avatar, extractMentionedUserIds, getChatErrorMessage]
  )

  // Send typing indicator
  const sendTypingIndicator = React.useCallback(
    (isTyping: boolean) => {
      if (!conversation?.id || !user?.id) return

      const participantUserIds = (conversation.participants || [])
        .filter(participant => participant.status === 'active' && participant.user.id !== user.id)
        .map(participant => participant.user.id)

      const userName = `${user.firstName} ${user.lastName}`.trim() || 'Someone'

      const sendTypingState = (typing: boolean) => {
        generalChatService
          .sendTypingIndicatorState(conversation.id, user.id, typing, userName, participantUserIds)
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('Failed to send typing indicator:', error)
          })
      }

      // User cleared all text -> stop typing immediately and reset refresh cycle.
      if (!isTyping) {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
          typingTimeoutRef.current = null
        }
        if (isCurrentUserTypingRef.current) {
          sendTypingState(false)
        }
        isCurrentUserTypingRef.current = false
        lastTypingActivityAtRef.current = 0
        return
      }

      // Track the latest typing activity timestamp on every keystroke.
      lastTypingActivityAtRef.current = Date.now()

      // Trigger typing only once when user starts typing.
      if (!isCurrentUserTypingRef.current) {
        sendTypingState(true)
        isCurrentUserTypingRef.current = true
        // While still typing, refresh typing status every minute.
        const scheduleRefresh = () => {
          typingTimeoutRef.current = setTimeout(() => {
            if (!isCurrentUserTypingRef.current) return
            const now = Date.now()
            const lastActivity = lastTypingActivityAtRef.current
            const hasRecentTypingActivity = now - lastActivity < CHAT_CONSTANTS.TYPING_REFRESH_INTERVAL

            if (!hasRecentTypingActivity) {
              sendTypingState(false)
              isCurrentUserTypingRef.current = false
              lastTypingActivityAtRef.current = 0
              typingTimeoutRef.current = null
              return
            }

            sendTypingState(true)
            scheduleRefresh()
          }, CHAT_CONSTANTS.TYPING_REFRESH_INTERVAL)
        }
        scheduleRefresh()
      }
    },
    [conversation?.id, conversation?.participants, user?.id, user?.firstName, user?.lastName]
  )

  React.useEffect(() => {
    isCurrentUserTypingRef.current = false
    lastTypingActivityAtRef.current = 0
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = null
    }
  }, [conversation?.id])

  React.useEffect(() => {
    return () => {
      Object.values(typingUserTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId))
      typingUserTimeoutsRef.current = {}
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = null
      }
      isCurrentUserTypingRef.current = false
      lastTypingActivityAtRef.current = 0
    }
  }, [])

  // Mark conversation as read
  const markConversationAsRead = React.useCallback(
    async (conversationId: number) => {
      if (!user?.id) return

      try {
        // Call the backend service to mark conversation as read
        await generalChatService.markConversationAsRead(conversationId)

        // Get the current unread count for this conversation
        const currentUnreadCount = unreadCounts[conversationId] || 0

        // Update the individual conversation's unread count to 0
        setUnreadCounts(prev => ({ ...prev, [conversationId]: 0 }))

        // Decrease the total unread count by the amount that was unread
        if (currentUnreadCount > 0) {
          decreaseUnreadCount(currentUnreadCount)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to mark conversation as read:', error)
      }
    },
    [user?.id, unreadCounts, decreaseUnreadCount]
  )

  // Delete message
  const deleteMessage = React.useCallback(
    async (messageId: number, scope: DeleteMessageScope) => {
      if (!user?.id) return

      try {
        await generalChatService.deleteMessage(messageId, { scope })
        setMessages(prev => prev.filter(msg => msg.id !== messageId))

        // Update conversation's last message info if the deleted message was the last one
        if (conversation?.id) {
          setConversations(prev =>
            prev.map(conv =>
              conv.id === conversation.id
                ? (() => {
                    // Find the next most recent message after deletion
                    const remainingMessages = messages.filter(msg => msg.id !== messageId)
                    const lastMessage = remainingMessages[remainingMessages.length - 1]

                    return {
                      ...conv,
                      lastMessageContent: lastMessage
                        ? formatLastMessageContent(lastMessage, user?.id || 0)
                        : undefined,
                      lastMessageAt: lastMessage?.createdAt || undefined,
                    }
                  })()
                : conv
            )
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete message:', error)
      }
    },
    [user?.id, conversation?.id, messages]
  )

  // Edit message
  const editMessage = React.useCallback(
    async (messageId: number, content: string) => {
      if (!user?.id || !content.trim()) return

      try {
        const updatedMessage = await generalChatService.updateMessage(messageId, { content: content.trim() })
        setMessages(prev =>
          prev.map(msg => (msg.id === messageId ? { ...msg, ...updatedMessage, isEdited: true } : msg))
        )

        // Check if this is the last message in the conversation and update conversation list if needed
        if (conversation?.id) {
          setConversations(prev => {
            const updatedConversations = prev.map(conv =>
              conv.id === conversation.id
                ? {
                    ...conv,
                    lastMessageContent: formatLastMessageContent(updatedMessage, user?.id || 0),
                    lastMessageAt: updatedMessage.updatedAt || updatedMessage.createdAt,
                  }
                : conv
            )
            return moveConversationToTop(updatedConversations, conversation.id)
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to edit message:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // React to message
  const reactToMessage = React.useCallback(
    async (messageId: number, reactionType: MessageReactionType, emojiData?: MessageEmojiData) => {
      if (!user?.id) return

      try {
        const currentMessage = messages.find(msg => msg.id === messageId)
        if (!currentMessage) return

        const existingReactions = currentMessage.reactions?.filter(r => r.user.id === user.id) || []
        const existingReactionOfSameType = existingReactions.find(r => {
          // For default reactions, only check reactionType
          if (reactionType !== 'custom') {
            return r.reactionType === reactionType
          }

          // For custom reactions, check both reactionType and emoji identifier
          if (r.reactionType !== 'custom') {
            return false
          }

          // Check if it's the same custom emoji by comparing identifiers
          if (emojiData?.unicode && r.emojiData?.unicode) {
            return r.emojiData.unicode === emojiData.unicode
          }

          if (emojiData?.imageUrl && r.emojiData?.imageUrl) {
            return r.emojiData.imageUrl === emojiData.imageUrl
          }

          // Fallback to name comparison if available
          if (emojiData?.name && r.emojiData?.name) {
            return r.emojiData.name === emojiData.name
          }

          return false
        })

        if (existingReactionOfSameType) {
          // Remove existing reaction of the same type
          await generalChatService.deleteMessageReaction({
            messageId,
            reactionType,
            emojiData,
          })

          // Update local state - remove the specific reaction
          setMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    reactions:
                      msg.reactions?.filter(r => {
                        // For default reactions, only check reactionType
                        if (reactionType !== 'custom') {
                          return !(r.user.id === user.id && r.reactionType === reactionType)
                        }

                        // For custom reactions, check both reactionType and emoji identifier
                        if (r.user.id !== user.id || r.reactionType !== 'custom') {
                          return true
                        }

                        // Check if it's the same custom emoji by comparing identifiers
                        if (emojiData?.unicode && r.emojiData?.unicode) {
                          return r.emojiData.unicode !== emojiData.unicode
                        }

                        if (emojiData?.imageUrl && r.emojiData?.imageUrl) {
                          return r.emojiData.imageUrl !== emojiData.imageUrl
                        }

                        // Fallback to name comparison if available
                        if (emojiData?.name && r.emojiData?.name) {
                          return r.emojiData.name !== emojiData.name
                        }

                        return true
                      }) || [],
                  }
                : msg
            )
          )
        } else {
          // User has no existing reactions - create new reaction
          const reactionDto: CreateMessageReactionDto = {
            messageId,
            reactionType,
            ...(emojiData && { emojiData }),
          }

          const newReaction = await generalChatService.createMessageReaction(reactionDto)

          // Update local state - append the new reaction to existing ones
          setMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    reactions: [...(msg.reactions || []), newReaction],
                  }
                : msg
            )
          )
        }

        // Mark conversation as read if it has unread messages
        if (conversation?.id && unreadCounts[conversation.id] > 0) {
          await markConversationAsRead(conversation.id)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to react to message:', error)
      }
    },
    [user?.id, messages, conversation?.id, unreadCounts, markConversationAsRead]
  )

  const pinMessage = React.useCallback(
    async (messageId: number, conversationId: number) => {
      if (!user?.id) return

      try {
        await generalChatService.pinMessage(conversationId, messageId)

        // Update local message state to add pin
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  pinnedMessage: {
                    pinnedAt: new Date().toISOString(),
                    pinnedByUser: {
                      id: user.id,
                      firstName: user.firstName,
                      lastName: user.lastName,
                      avatar: user.avatar,
                      profileId: user.profileId,
                    },
                  },
                }
              : msg
          )
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to pin message:', error)
      }
    },
    [user?.id, user?.firstName, user?.lastName, user?.avatar, user?.profileId]
  )

  const unpinMessage = React.useCallback(
    async (messageId: number, conversationId: number) => {
      if (!user?.id) return

      try {
        await generalChatService.unpinMessage(conversationId, messageId)

        // Update local message state to remove pin
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  pinnedMessage: undefined,
                }
              : msg
          )
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to unpin message:', error)
      }
    },
    [user?.id]
  )

  const getPinnedMessages = React.useCallback(
    async (conversationId: number): Promise<PinnedMessage[]> => {
      if (!user?.id) return []

      try {
        const pinnedMessages = await generalChatService.getPinnedMessages(conversationId)
        return pinnedMessages
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to get pinned messages:', error)
        return []
      }
    },
    [user?.id]
  )

  // Create new conversation
  const createConversation = React.useCallback(
    async (participantIds: number[], name?: string) => {
      if (!user?.id) return

      setIsCreatingConversation(true)
      try {
        const conversationData: CreateConversationDto = {
          participantIds: [...participantIds, user.id],
          type: participantIds.length === 1 ? ConversationType.DIRECT : ConversationType.GROUP,
          name: name, // Only send name if explicitly provided
        }

        const newConversation = await generalChatService.createConversation(conversationData)

        // Add the new conversation to the list with initial empty message state
        const conversationWithInitialState = {
          ...newConversation,
          lastMessageContent: undefined,
          lastMessageAt: undefined,
        }

        setConversations(prev => [conversationWithInitialState, ...prev])

        // Auto-select the new conversation
        setConversation(conversationWithInitialState)

        return newConversation
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create conversation:', error)
        throw error
      } finally {
        setIsCreatingConversation(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id]
  )

  // Create new group chat
  const createGroupChat = React.useCallback(
    async (
      name: string,
      description: string,
      participantIds: number[],
      type: ConversationType = ConversationType.GROUP,
      addAllUsers?: boolean,
      courseIds?: number[]
    ) => {
      if (!user?.id) return

      setIsCreatingConversation(true)
      try {
        const conversationData: CreateConversationDto = {
          participantIds: addAllUsers ? [user.id] : [...participantIds, user.id], // Only include current user if adding all users
          type: type,
          name: name,
          description: description,
          addAllUsers: addAllUsers,
          courseIds: courseIds,
        }

        const newConversation = await generalChatService.createConversation(conversationData)

        // Add the new conversation to the list with initial empty message state
        const conversationWithInitialState = {
          ...newConversation,
          lastMessageContent: undefined,
          lastMessageAt: undefined,
        }

        setConversations(prev => [conversationWithInitialState, ...prev])

        // Auto-select the new conversation
        setConversation(conversationWithInitialState)

        return newConversation
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create group chat:', error)
        throw error
      } finally {
        setIsCreatingConversation(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id]
  )

  // Update conversation
  const updateConversation = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (conversationId: number, updates: any) => {
      if (!user?.id) return

      try {
        const updatedConversation = await generalChatService.updateConversation(conversationId, updates)

        // Update both the conversations list and the current conversation if it matches
        setConversations(prev => prev.map(conv => (conv.id === conversationId ? updatedConversation : conv)))
        if (conversation?.id === conversationId) {
          setConversation(updatedConversation)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update conversation:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // Delete conversation
  const deleteConversation = React.useCallback(
    async (conversationId: number, permanent = false) => {
      if (!user?.id) return

      try {
        await generalChatService.deleteConversation(conversationId, permanent)

        // Remove from conversations list
        setConversations(prev => prev.filter(conv => conv.id !== conversationId))

        // Clear current conversation if it was the deleted one
        if (conversation?.id === conversationId) {
          setConversation(null)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete conversation:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // Pin conversation
  const pinConversation = React.useCallback(
    async (conversationId: number) => {
      if (!user?.id) return

      try {
        await generalChatService.pinConversation(conversationId)

        // Update the conversation's pinned status and move it to the top
        setConversations(prev => {
          const pinnedIndex = prev.findIndex(conv => conv.id === conversationId)
          if (pinnedIndex === -1) return prev

          const pinnedConversation = { ...prev[pinnedIndex], isPinned: true }
          const otherConversations = prev.filter((_, index) => index !== pinnedIndex)

          return [pinnedConversation, ...otherConversations]
        })

        // Update current conversation if it matches
        if (conversation?.id === conversationId) {
          setConversation(prev => (prev ? { ...prev, isPinned: true } : null))
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to pin conversation:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // Unpin conversation
  const unpinConversation = React.useCallback(
    async (conversationId: number) => {
      if (!user?.id) return

      try {
        await generalChatService.unpinConversation(conversationId)

        // Update the conversation's pinned status and move it back to its natural position
        setConversations(prev => {
          const unpinnedIndex = prev.findIndex(conv => conv.id === conversationId)
          if (unpinnedIndex === -1) return prev

          const unpinnedConversation = { ...prev[unpinnedIndex], isPinned: false }
          const otherConversations = prev.filter((_, index) => index !== unpinnedIndex)

          // Find the correct position for the unpinned conversation among unpinned conversations
          const unpinnedConversations = otherConversations.filter(conv => !conv.isPinned)
          const pinnedConversations = otherConversations.filter(conv => conv.isPinned)

          // Sort unpinned conversations by updatedAt DESC, then lastMessageAt DESC
          const sortedUnpinned = [...unpinnedConversations, unpinnedConversation].sort((a, b) => {
            const aUpdatedAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
            const bUpdatedAt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
            if (aUpdatedAt !== bUpdatedAt) return bUpdatedAt - aUpdatedAt

            const aLastMessageAt = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
            const bLastMessageAt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
            return bLastMessageAt - aLastMessageAt
          })

          // Return pinned conversations first, then sorted unpinned conversations
          return [...pinnedConversations, ...sortedUnpinned]
        })

        // Update current conversation if it matches
        if (conversation?.id === conversationId) {
          setConversation(prev => (prev ? { ...prev, isPinned: false } : null))
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to unpin conversation:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // Search conversations
  const searchConversations = React.useCallback(
    async (query: string) => {
      if (!user?.id || !query.trim()) return []

      try {
        const results = await generalChatService.searchConversations(query)
        return results
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to search conversations:', error)
        return []
      }
    },
    [user?.id]
  )

  // Search messages globally
  const searchMessages = React.useCallback(
    async (query: string) => {
      if (!user?.id || !query.trim()) return []

      try {
        const results = await generalChatService.searchMessages(query)
        return results.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to search messages:', error)
        return []
      }
    },
    [user?.id]
  )

  // Add participants
  const addParticipants = React.useCallback(
    async (conversationId: number, participantIds: number[]) => {
      if (!user?.id) return

      try {
        await generalChatService.addParticipants(conversationId, participantIds)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to add participants:', error)
      }
    },
    [user?.id]
  )

  // Remove participants
  const removeParticipants = React.useCallback(
    async (conversationId: number, participantIds: number[]) => {
      if (!user?.id) return

      try {
        await generalChatService.removeParticipants(conversationId, participantIds)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to remove participants:', error)
      }
    },
    [user?.id]
  )

  // Leave conversation
  const leaveConversation = React.useCallback(
    async (conversationId: number) => {
      if (!user?.id) return

      try {
        await generalChatService.leaveConversation(conversationId)
        // Remove from conversations list
        setConversations(prev => prev.filter(conv => conv.id !== conversationId))
        // Clear current conversation if it was the deleted one
        if (conversation?.id === conversationId) {
          setConversation(null)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to leave conversation:', error)
      }
    },
    [user?.id, conversation?.id]
  )

  // Update participant role
  const updateParticipantRole = React.useCallback(
    async (conversationId: number, participantId: number, newRole: ParticipantRole) => {
      if (!user?.id) return

      try {
        await generalChatService.updateParticipantRole({
          conversationId,
          participantId,
          newRole,
        })
        // Update the conversation's participants list
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  participants: conv.participants?.map(participant =>
                    participant.user.id === participantId ? { ...participant, role: newRole } : participant
                  ),
                }
              : conv
          )
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update participant role:', error)
      }
    },
    [user?.id]
  )

  // Connection management
  const connect = React.useCallback(() => {
    generalChatService.initialize()
  }, [])

  const disconnect = React.useCallback(() => {
    generalChatService.disconnect()
  }, [])

  const reconnect = React.useCallback(() => {
    generalChatService.disconnect()
    setTimeout(() => {
      generalChatService.initialize()
    }, 1000)
  }, [])

  // Reset pagination state (useful for debugging or manual refresh)
  const resetPagination = React.useCallback(() => {
    setTotalMessagesLoaded(0)
    setHasPreviousMessages(false)
    setHasMoreMessages(false)
    setContextMessagesEndIndex(null)
    setMessages([])
  }, [])

  // Load conversations and unread counts when user is authenticated
  React.useEffect(() => {
    if (user?.id && !hasLoadedConversations) {
      loadConversations()
    }
  }, [user?.id, hasLoadedConversations, loadConversations]) // Removed loadConversations from dependencies

  // Subscribe to WebSocket events for chat
  React.useEffect(() => {
    if (!user?.id) return

    // Subscribe to chat events
    subscribe(WEBSOCKET_EVENTS.NEW_MESSAGE, handleNewMessage)
    subscribe(WEBSOCKET_EVENTS.TYPING_INDICATOR, handleTypingIndicator)
    subscribe('typing_indicator', handleTypingIndicator)
    subscribe(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent)
    subscribe(WEBSOCKET_EVENTS.MESSAGE_DELIVERED, handleMessageDelivered)
    subscribe(WEBSOCKET_EVENTS.MESSAGE_REACTION, handleMessageReaction)
    subscribe(WEBSOCKET_EVENTS.MESSAGE_REACTION_BROADCAST, handleMessageReactionBroadcast)

    return () => {
      unsubscribe(WEBSOCKET_EVENTS.NEW_MESSAGE, handleNewMessage)
      unsubscribe(WEBSOCKET_EVENTS.TYPING_INDICATOR, handleTypingIndicator)
      unsubscribe('typing_indicator', handleTypingIndicator)
      unsubscribe(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent)
      unsubscribe(WEBSOCKET_EVENTS.MESSAGE_DELIVERED, handleMessageDelivered)
      unsubscribe(WEBSOCKET_EVENTS.MESSAGE_REACTION, handleMessageReaction)
      unsubscribe(WEBSOCKET_EVENTS.MESSAGE_REACTION_BROADCAST, handleMessageReactionBroadcast)
    }
  }, [
    user?.id,
    subscribe,
    unsubscribe,
    handleNewMessage,
    handleTypingIndicator,
    handleMessageSent,
    handleMessageDelivered,
    handleMessageReaction,
    handleMessageReactionBroadcast,
  ])

  // Update local state when ref changes
  React.useEffect(() => {
    if (chatPopupRef.current) {
      setTotalUnreadCount(chatPopupRef.current.totalUnreadCount)
      setIsConnected(chatPopupRef.current.isConnected)
    }
  }, [chatPopupRef.current?.totalUnreadCount, chatPopupRef.current?.isConnected])

  // Update connection status when socket connection changes
  React.useEffect(() => {
    setIsConnected(socketConnected)
  }, [socketConnected])

  React.useEffect(() => {
    return () => {
      clearPendingScrollTimeout()
    }
  }, [clearPendingScrollTimeout])

  // Cleanup chat state when user logs out
  React.useEffect(() => {
    if (!user?.id) {
      clearPendingScrollTimeout()
      setPendingScrollToMessage(null)
      setTotalUnreadCount(0)
      setIsConnected(false)
      setConversations([])
      setConversation(null)
      setMessages([])
      setUnreadCounts({})
      setHasPreviousMessages(false)
      setHasMoreMessages(false)
      setContextMessagesEndIndex(null)
      setTotalMessagesLoaded(0)
      setHasLoadedConversations(false)
    }
  }, [user?.id, clearPendingScrollTimeout])

  // Load messages when conversation changes AND chat is open
  React.useEffect(() => {
    if (conversation?.id && isChatOpen) {
      const isPendingNavigation =
        pendingScrollToMessage &&
        (!pendingScrollToMessage.targetConversationId ||
          pendingScrollToMessage.targetConversationId === conversation.id)

      if (isPendingNavigation && pendingScrollToMessage) {
        if (messageNavigationLoadStartedRef.current !== pendingScrollToMessage.messageId) {
          messageNavigationLoadStartedRef.current = pendingScrollToMessage.messageId
          loadMessages(pendingScrollToMessage.messageId)
        }
        return
      }

      messageNavigationLoadStartedRef.current = null
      loadMessages(undefined)
    } else if (!conversation?.id) {
      setMessages([])
      setTotalMessagesLoaded(0)
      previousCursorRef.current = null
      setHasPreviousMessages(false)
      setHasMoreMessages(false)
      setContextMessagesEndIndex(null)
      messageNavigationLoadStartedRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, isChatOpen, pendingScrollToMessage?.messageId, pendingScrollToMessage?.targetConversationId])

  // Scroll to a pending message after its context has loaded.
  React.useEffect(() => {
    if (!pendingScrollToMessage || !conversation?.id || !isChatOpen) return
    if (
      pendingScrollToMessage.targetConversationId &&
      pendingScrollToMessage.targetConversationId !== conversation.id
    ) {
      return
    }
    if (isLoading || isLoadingContextMessages) return

    const { messageId } = pendingScrollToMessage
    const existingMessage = messages.find(msg => msg.id === messageId)
    if (!existingMessage) return

    completePendingMessageNavigation(messageId)
    finishPendingMessageNavigation()
  }, [
    pendingScrollToMessage,
    conversation?.id,
    isChatOpen,
    messages,
    isLoading,
    isLoadingContextMessages,
    completePendingMessageNavigation,
    finishPendingMessageNavigation,
  ])

  // Load conversation details when conversation changes
  React.useEffect(() => {
    if (conversation?.id) {
      const selectedConversation = conversations.find(c => c.id === conversation.id)
      setConversation(selectedConversation || null)
    } else {
      setConversation(null)
    }
  }, [conversation?.id, conversations])

  // Auto-select conversation with most recent updatedAt when conversations are loaded and no conversation is selected
  React.useEffect(() => {
    if (conversations.length > 0 && !conversation && !isLoading) {
      // Auto-select the conversation with the most recent updatedAt if none is selected and not currently loading
      const mostRecentConversation = conversations.reduce((latest, current) => {
        const latestUpdatedAt = new Date(latest.updatedAt).getTime()
        const currentUpdatedAt = new Date(current.updatedAt).getTime()
        return currentUpdatedAt > latestUpdatedAt ? current : latest
      })
      setConversation(mostRecentConversation)
    }
  }, [conversations, conversation, isLoading])

  // Join chat room when conversation is selected
  React.useEffect(() => {
    if (conversation?.id && user?.id && isConnected) {
      generalChatService.joinChatRoom({
        conversationId: conversation.id,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userAvatar: user.avatar,
        isJobOwnerOrAdmin: false, // General chat doesn't have job owner concept
      })
    }
  }, [conversation?.id, user?.id, isConnected, user?.firstName, user?.lastName, user?.avatar])

  const contextValue: ChatContextType = React.useMemo(
    () => ({
      // Chat popup control
      openChat: (
        targetUserId?: number,
        targetUserName?: string,
        conversationId?: number,
        jobName?: string,
        targetUserAvatar?: string,
        isLoadSelectedMessage?: boolean,
        messageId?: number
      ) => {
        setIsChatOpen(true)

        if (conversationId) {
          const selectedConversation = conversations.find(conv => conv.id === conversationId)
          if (selectedConversation) {
            setConversation(selectedConversation)
          }
        }

        if (isLoadSelectedMessage && messageId && conversationId) {
          clearPendingScrollTimeout()
          messageNavigationLoadStartedRef.current = null
          chatPopupRef.current.beginMessageNavigationScroll?.()
          setPendingScrollToMessage({
            messageId,
            targetConversationId: conversationId,
          })
        } else {
          clearPendingScrollTimeout()
          clearHighlightTimer()
          setPendingScrollToMessage(null)
          setHighlightedMessageId(null)
          messageNavigationLoadStartedRef.current = null
        }

        chatPopupRef.current.openChat(targetUserId, targetUserName, conversationId, jobName, targetUserAvatar)
      },

      // Connection and unread count state
      totalUnreadCount,
      isConnected,

      // Chat state management
      conversations,
      conversation,
      messages,
      isTyping,
      typingUserName,
      unreadCounts,
      hasPreviousMessages,
      hasMoreMessages,
      contextMessagesEndIndex,
      isLoading,
      isLoadingPrevious,
      isLoadingMore,
      isPreviousMessageLoad,
      isCreatingConversation,
      hasLoadedConversations,
      highlightedMessageId,
      isLoadingContextMessages,
      pendingScrollToMessage,

      // Unread count management
      increaseUnreadCount,
      decreaseUnreadCount,
      decreaseUnreadCountByConversation,

      // Message actions
      sendMessage,
      sendMessageWithFile,
      sendReplyMessage,
      sendTypingIndicator,
      markConversationAsRead,
      deleteMessage,
      editMessage,
      reactToMessage,
      pinMessage,
      unpinMessage,
      getPinnedMessages,

      // Conversation actions
      loadConversations, // Use this for immediate loading (e.g., initial load)
      debouncedLoadConversations, // Use this for external calls to prevent excessive API calls
      loadPreviousMessages,
      loadMoreMessages,
      createConversation,
      createGroupChat,
      updateConversation,
      deleteConversation,
      pinConversation,
      unpinConversation,
      searchConversations,
      searchMessages,
      reloadConversations,

      // Participant actions
      addParticipants,
      removeParticipants,
      leaveConversation,
      updateParticipantRole,

      // Connection management
      connect,
      disconnect,
      reconnect,

      // Utility functions
      resetPagination,

      // State setters for external updates
      setConversations,
      setConversation,
      setConversationId: (conversationId: number | undefined) => {
        if (conversationId) {
          const selectedConversation = conversations.find(conv => conv.id === conversationId)
          setConversation(selectedConversation || null)
        } else {
          setConversation(null)
        }
      },
      setIsChatOpen,

      // WebSocket event handlers
      handleNewMessage,
      handleTypingIndicator,
      handleMessageDelivered,
      handleMessageReaction,
      handleMessageReactionBroadcast,

      // Message navigation
      scrollToMessage,
      scrollToConversation,
    }),
    [
      totalUnreadCount,
      isConnected,
      conversations,
      conversation,
      messages,
      isTyping,
      typingUserName,
      unreadCounts,
      hasPreviousMessages,
      hasMoreMessages,
      contextMessagesEndIndex,
      isLoading,
      isLoadingPrevious,
      isLoadingMore,
      isPreviousMessageLoad,
      isCreatingConversation,
      hasLoadedConversations,
      increaseUnreadCount,
      decreaseUnreadCount,
      decreaseUnreadCountByConversation,
      sendMessage,
      sendMessageWithFile,
      sendReplyMessage,
      sendTypingIndicator,
      markConversationAsRead,
      deleteMessage,
      editMessage,
      reactToMessage,
      pinMessage,
      unpinMessage,
      getPinnedMessages,
      loadConversations,
      debouncedLoadConversations,
      loadPreviousMessages,
      loadMoreMessages,
      createConversation,
      createGroupChat,
      updateConversation,
      deleteConversation,
      pinConversation,
      unpinConversation,
      searchConversations,
      searchMessages,
      reloadConversations,
      addParticipants,
      removeParticipants,
      leaveConversation,
      updateParticipantRole,
      connect,
      disconnect,
      reconnect,
      resetPagination,
      setConversations,
      setConversation,
      handleNewMessage,
      handleTypingIndicator,
      handleMessageDelivered,
      handleMessageReaction,
      handleMessageReactionBroadcast,
      scrollToMessage,
      scrollToConversation,
      highlightedMessageId,
      isLoadingContextMessages,
      pendingScrollToMessage,
      clearHighlightTimer,
      clearPendingScrollTimeout,
    ]
  )

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
      <GeneralMultiChatPopup ref={chatPopupRef} />
    </ChatContext.Provider>
  )
}

export const useChat = (): ChatContextType => {
  const context = React.useContext(ChatContext)
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}
